import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ChatbotWidget from '../../components/ui/ChatbotWidget';
import InstitutionHeader from './components/InstitutionHeader';
import TabNavigation from './components/TabNavigation';
import OverviewTab from './components/OverviewTab';
import ProgramsTab from './components/ProgramsTab';
import AdmissionsTab from './components/AdmissionsTab';
import FeesTab from './components/FeesTab';
import CareersTab from './components/CareersTab';
import Icon from '../../components/AppIcon';

/**
 * Mappe un enregistrement schools (Supabase) vers le format
 * attendu par les composants d'onglets (InstitutionHeader, OverviewTab…).
 */
const mapDbToInstitution = (s) => ({
  id:              s.id,
  name:            s.name,
  shortName:       s.short_name || s.name,
  location:        `${s.city}, Maroc`,
  type:            s.type === 'Public' ? 'École publique' : 'École privée',
  logo:            s.logo_url || null,
  website:         s.website || null,
  studentCount:    s.student_count ? s.student_count.toLocaleString('fr-MA') : 'N/A',
  establishedYear: s.founded_year?.toString() || 'N/A',
  programCount:    Array.isArray(s.programs) ? s.programs.length.toString() : 'N/A',
  employmentRate:  s.employment_rate?.toString() || 'N/A',
  description:     s.description || '',

  // Onglet Vue d'ensemble
  facilities:     s.facilities     || [],
  accreditations: s.accreditations || [],
  gallery:        [],

  // Onglet Programmes
  programs: s.programs || [],
  applicationProcess: [],
  requiredDocuments:  [],

  // Onglet Admissions
  admissionThresholds: [],
  admissionStats: [
    { label: "Seuil d'admission",  value: s.admission_threshold ? `${s.admission_threshold}/20` : 'N/A' },
    { label: "Type de concours",   value: s.admission_type || 'N/A' },
    { label: "Bacs acceptés",      value: s.accepted_bac_types?.join(', ') || 'N/A' },
  ],
  importantDates: [],

  // Onglet Frais
  tuitionFees: [{
    program:      s.category || 'Formation',
    annual:       (s.annual_fees || 0).toLocaleString('fr-MA'),
    registration: (s.registration_fees || 0).toLocaleString('fr-MA'),
    total:        ((s.annual_fees || 0) + (s.registration_fees || 0)).toLocaleString('fr-MA'),
  }],
  additionalCosts: [],
  paymentPlans:    [],
  financialAid:    s.financial_aid || [],

  // Onglet Carrières
  employmentBySector:  s.employment_by_sector  || [],
  salaryByExperience:  s.salary_by_experience  || [],
  employmentMetrics: [
    { label: "Taux d'emploi à 6 mois",    value: s.employment_rate ? `${s.employment_rate}%` : 'N/A' },
    { label: "Salaire moyen de départ",   value: s.avg_starting_salary ? `${s.avg_starting_salary.toLocaleString('fr-MA')} MAD/an` : 'N/A' },
  ],
  topEmployers: s.top_employers || [],
  careerPaths:  s.career_paths  || [],
  alumniStories: [],
});

const InstitutionDetails = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInstitution = async () => {
      const id = searchParams.get('id');

      if (!id) {
        setError('no_id');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data, error: dbError } = await supabase
          .from('schools')
          .select('*')
          .eq('id', id)
          .single();

        if (dbError) throw dbError;
        if (!data) throw new Error('Établissement introuvable');

        setInstitution(mapDbToInstitution(data));
      } catch (err) {
        console.error('InstitutionDetails fetch error :', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInstitution();
  }, [searchParams]);

  const customBreadcrumbs = [
    { label: 'Accueil',            path: '/homepage' },
    { label: 'Écoles & Universités', path: '/post-bac-database' },
    { label: institution?.shortName || 'Établissement', path: '/institution-details', isLast: true },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':    return <OverviewTab    institution={institution} />;
      case 'programs':    return <ProgramsTab    institution={institution} />;
      case 'admissions':  return <AdmissionsTab  institution={institution} />;
      case 'fees':        return <FeesTab        institution={institution} />;
      case 'careers':     return <CareersTab     institution={institution} />;
      default:            return <OverviewTab    institution={institution} />;
    }
  };

  // ── États de chargement / erreur ─────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8 flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement des détails de l'établissement…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error === 'no_id') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <Icon name="School" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
            Aucun établissement sélectionné
          </h2>
          <p className="text-muted-foreground mb-6">
            Naviguez depuis la base de données pour voir la fiche d'un établissement.
          </p>
          <Link
            to="/post-bac-database"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth"
          >
            <Icon name="ArrowLeft" size={16} />
            Retour à la base de données
          </Link>
        </div>
      </div>
    );
  }

  if (error || !institution) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
            Établissement non trouvé
          </h2>
          <p className="text-muted-foreground mb-6">
            {error || "L'établissement demandé n'existe pas ou n'est plus disponible."}
          </p>
          <Link
            to="/post-bac-database"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth"
          >
            <Icon name="ArrowLeft" size={16} />
            Retour à la base de données
          </Link>
        </div>
      </div>
    );
  }

  // ── Rendu principal ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <BreadcrumbNavigation customBreadcrumbs={customBreadcrumbs} />

        <InstitutionHeader institution={institution} />

        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="mb-8">
          {renderTabContent()}
        </div>
      </main>
      <ChatbotWidget />
    </div>
  );
};

export default InstitutionDetails;
